import { Box, HStack, VStack, Icon, Heading, Link, List, ListItem } from '@chakra-ui/react';
import { FaFileContract } from 'react-icons/fa';
import { IContrat } from '@/service/club/types';
import { getContractDisplayFields } from '@/utils/contractDisplay';
import { Label } from '@/components/atoms/Label';

interface ContractCardProps {
  contrato: IContrat;
  colors: { primaryColor: string };
  minWidth?: string;
  flexShrink?: number;
  onClick?: () => void;
  isSelected?: boolean;
  onBoletoClick?: (url: string) => void;
}

export function ContractCard({ contrato, colors, minWidth, flexShrink, onClick, isSelected, onBoletoClick }: ContractCardProps) {
  const displayFields = getContractDisplayFields(contrato);
  const hasContent =
    displayFields.length > 0 || (contrato.produtos?.length ?? 0) > 0 || !!contrato.link;
  if (!hasContent) return null;

  const valorField = displayFields.find((f) => f.key === 'valor_servico');
  const headerFields = displayFields.filter((f) =>
    ['numero_contrato', 'codigo_contrato', 'nome_servico', 'nome_contrato'].includes(f.key)
  );
  const detailFields = displayFields.filter(
    (f) =>
      !['numero_contrato', 'codigo_contrato', 'nome_servico', 'nome_contrato', 'valor_servico'].includes(f.key)
  );

  return (
    <Box
      as={onClick ? 'button' : 'div'}
      onClick={onClick}
      minWidth={minWidth}
      flexShrink={flexShrink}
      p={5}
      borderRadius="xl"
      border="2px solid"
      borderColor={isSelected ? colors.primaryColor : 'gray.200'}
      backgroundColor={isSelected ? `${colors.primaryColor}08` : 'white'}
      position="relative"
      overflow="hidden"
      transition="all 0.2s ease"
      textAlign="left"
      width="100%"
      cursor={onClick ? 'pointer' : 'default'}
      _hover={
        onClick
          ? {
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              borderColor: isSelected ? colors.primaryColor : 'gray.300',
              transform: 'translateY(-2px)',
            }
          : undefined
      }
      _before={{
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: colors.primaryColor,
        borderRadius: '4px 0 0 4px',
      }}
    >
      {isSelected && (
        <Box
          position="absolute"
          top="12px"
          right="12px"
          backgroundColor={colors.primaryColor}
          color="white"
          px="10px"
          py="4px"
          borderRadius="full"
          fontSize="xs"
          fontWeight="600"
          textTransform="uppercase"
          letterSpacing="wider"
          zIndex={1}
        >
          Selecionado
        </Box>
      )}
      <VStack align="stretch" spacing={4}>
        <HStack spacing={3} align="flex-start">
          <Box
            p={2}
            borderRadius="lg"
            backgroundColor={`${colors.primaryColor}12`}
            color={colors.primaryColor}
            flexShrink={0}
          >
            <Icon as={FaFileContract} boxSize={4} />
          </Box>
          <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
            {headerFields.map(({ key, label, value }) => (
              <Label key={key} fontSize="sm" color="gray.600" fontWeight="500" noOfLines={2} lineHeight="1.3">
                <span style={{ color: 'var(--chakra-colors-gray-400)', fontWeight: 400 }}>{label}: </span>
                {value}
              </Label>
            ))}
          </VStack>
        </HStack>

        {valorField && (
          <Box
            py={3}
            px={4}
            borderRadius="lg"
            backgroundColor="gray.50"
            border="1px solid"
            borderColor="gray.100"
          >
            <Label fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>
              {valorField.label}
            </Label>
            <Heading size="md" color={colors.primaryColor} fontWeight="700">
              {valorField.value}
            </Heading>
          </Box>
        )}

        {(detailFields.length > 0 || contrato.produtos?.length || contrato.link) && (
          <Box pt={2} borderTop="1px solid" borderColor="gray.100">
            <VStack align="stretch" spacing={2}>
              {detailFields.map(({ key, label, value }) => (
                <HStack key={key} justify="space-between" align="flex-start" spacing={2}>
                  <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                    {label}:
                  </Label>
                  <Label fontSize="sm" color="gray.800" fontWeight="600" textAlign="right" noOfLines={2}>
                    {value}
                  </Label>
                </HStack>
              ))}
              {contrato.produtos && contrato.produtos.length > 0 && (
                <Box width="100%">
                  <Label fontSize="xs" color="gray.500" fontWeight="600" mb={1} display="block">
                    Produtos
                  </Label>
                  <List spacing={0.5} fontSize="sm" color="gray.700">
                    {contrato.produtos.map((item, i) => (
                      <ListItem key={i} display="flex" alignItems="flex-start" gap={2}>
                        <Box as="span" color={colors.primaryColor} mt="3px" flexShrink={0}>
                          •
                        </Box>
                        <span>{item}</span>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {contrato.link && onBoletoClick && (
                <HStack justify="space-between" align="flex-start" spacing={2}>
                  <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                    Contrato:
                  </Label>
                  <Link
                    fontSize="sm"
                    fontWeight="600"
                    color={colors.primaryColor}
                    textDecoration="underline"
                    textAlign="right"
                    _hover={{ textDecoration: 'underline', opacity: 0.8 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onBoletoClick(contrato.link!);
                    }}
                  >
                    Visualizar Contrato
                  </Link>
                </HStack>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
